import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Input, Radio, Space, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import AddBrandImage from '../../services/AddBrandImage';

const { Title, Text } = Typography;

export default function CompanyDetails() {
  const authContext = useAuth();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [imageData, setImageData] = useState({});
  const brandImageInputRef = useRef(null);
  const brandImageInputId = 'company-details-brand-image-input';

  const [defaultLocIndex, setDefaultLocIndex] = useState(-1);

  const currentRole = authContext.role || authContext.additionalUserInfo?.role || '';
  const canEditBusinessDetails = ['ROLE_CMANAGER', 'ROLE_SADMIN', 'ROLE_FWORKER'].includes(currentRole);
  const maxFileSize = 10 * 1024 * 1024;

  useEffect(() => {
    const company = authContext.additionalUserInfo?.company || {};
    const existingLocations = Array.isArray(company.locations) ? company.locations : [];
    const defaultIdx = existingLocations.findIndex((l) => l.defaultLocation === 'Y');
    setDefaultLocIndex(defaultIdx);
    const existingPhoneNumbers = Array.isArray(company.companyPhoneNumbers) ? company.companyPhoneNumbers : [];
    form.setFieldsValue({
      companyName: company.companyName || '',
      companyABN: company.companyABN || '',
      brandImageUrl: company.brandImageUrl || '',
      companyId: company.id || company.companyId || '',
      companyPhoneNumbers: existingPhoneNumbers.map((ph) => ({ number: ph })),
      locations: existingLocations.map((loc) => ({
        id: loc.id || '',
        streetNo: loc.streetNo || '',
        streetName: loc.streetName || '',
        city: loc.city || '',
        country: loc.country || '',
        suburb: loc.suburb || '',
        state: loc.state || '',
        postCode: loc.postCode || '',
        defaultLocation: loc.defaultLocation || '',
      })),
    });
  }, [authContext.additionalUserInfo, form]);

  const pickImage = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > maxFileSize) {
      setError('The file size is too large. Max 10 MB.');
      event.target.value = '';
      return;
    }

    const previousLocalUrl = imageData?.localUrl;
    const localUrl = URL.createObjectURL(file);

    setError('');
    setSaved(false);
    setUploadingImage(true);
    setImageData({ file, localUrl, fileName: file.name, type: file.type, uploading: true });

    try {
      const uploadForm = new FormData();
      uploadForm.append('file', file);

      const brandUpload = await AddBrandImage(uploadForm, authContext.token);
      const brandImageUrl = brandUpload?.brandImageUrl || brandUpload?.data?.brandImageUrl || '';
      const brandImageKeyName = brandUpload?.brandImageKeyName || brandUpload?.data?.brandImageKeyName || '';

      if (!brandImageUrl) {
        throw new Error('Brand image upload did not return a URL');
      }

      form.setFieldsValue({
        brandImageUrl,
      });
      setImageData({
        file,
        localUrl,
        fileName: file.name,
        type: file.type,
        brandImageUrl,
        brandImageKeyName,
      });
      if (previousLocalUrl) {
        URL.revokeObjectURL(previousLocalUrl);
      }
    } catch (uploadError) {
      console.error(uploadError);
      URL.revokeObjectURL(localUrl);
      setImageData({});
      setError('Failed to upload brand image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const removeImage = () => {
    if (imageData?.localUrl) {
      URL.revokeObjectURL(imageData.localUrl);
    }
    setImageData({});
    form.setFieldValue('brandImageUrl', '');
    if (brandImageInputRef.current) {
      brandImageInputRef.current.value = '';
    }
  };

  const openBrandImagePicker = () => {
    const input = brandImageInputRef.current;
    if (!input) {
      return;
    }

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.click();
  };

  useEffect(() => {
    return () => {
      if (imageData?.localUrl) {
        URL.revokeObjectURL(imageData.localUrl);
      }
    };
  }, [imageData?.localUrl]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');
      setSaved(false);

      const submittedLocations = Array.isArray(values.locations) ? values.locations : [];
      const locations = submittedLocations
        .map((loc) => ({
          ...loc,
          streetNo: (loc.streetNo || '').trim(),
          streetName: (loc.streetName || '').trim(),
          city: (loc.city || '').trim(),
          country: (loc.country || '').trim(),
          suburb: (loc.suburb || '').trim(),
          state: (loc.state || '').trim(),
          postCode: (loc.postCode || '').trim(),
          defaultLocation: loc.defaultLocation || '',
        }))
        .filter((loc) => loc.streetNo || loc.streetName || loc.city || loc.country || loc.suburb || loc.state || loc.postCode || loc.id);

      const submittedPhoneNumbers = Array.isArray(values.companyPhoneNumbers) ? values.companyPhoneNumbers : [];
      const companyPhoneNumbers = submittedPhoneNumbers
        .map((ph) => (ph?.number || '').trim())
        .filter(Boolean);

      const payload = {
        id: values.companyId || authContext.additionalUserInfo?.company?.id || authContext.additionalUserInfo?.company?.companyId,
        companyName: values.companyName,
        companyABN: values.companyABN,
        brandImageUrl: values.brandImageUrl || '',
        locations,
        companyPhoneNumbers,
      };

      if (imageData?.brandImageUrl || values.brandImageUrl) {
        payload.brandImageUrl = imageData?.brandImageUrl || values.brandImageUrl;
      }

      if (imageData?.brandImageKeyName) {
        payload.brandImageKeyName = imageData.brandImageKeyName;
      }

      const response = await fetch(`${baseUrl_SPB}/api/v1/company/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authContext.token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Company save failed');
      }

      const mergedCompany = {
        ...(authContext.additionalUserInfo?.company || {}),
        ...payload,
        id: payload.id || authContext.additionalUserInfo?.company?.id || authContext.additionalUserInfo?.company?.companyId,
        companyId: payload.id || authContext.additionalUserInfo?.company?.companyId || authContext.additionalUserInfo?.company?.id,
      };

      if (authContext.setAdditionalUserInfo) {
        authContext.setAdditionalUserInfo((previousUser) => ({
          ...(previousUser || {}),
          company: mergedCompany,
          brandImageUrl: mergedCompany.brandImageUrl || previousUser?.brandImageUrl || '',
        }));
      }

      const meResponse = await fetch(`${baseUrl_SPB}/api/v1/user/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authContext.token,
        },
      });

      if (meResponse.ok) {
        const refreshedUser = await meResponse.json();
        authContext.setAdditionalUserInfo && authContext.setAdditionalUserInfo((previousUser) => ({
          ...(previousUser || {}),
          ...(refreshedUser || {}),
          
        }));
        authContext.setRole && authContext.setRole(refreshedUser?.role || '');
      }

      setSaved(true);
    } catch (saveError) {
      if (saveError?.errorFields) {
        return;
      }
      console.error(saveError);
      setError('Failed to update company details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Company Details</Title>
          <Text type="secondary">Update your existing company profile details.</Text>
        </div>

        {!canEditBusinessDetails ? <Alert type="info" showIcon message="You do not have permission to edit business details." /> : null}

        {error ? <Alert type="error" showIcon message={error} /> : null}
        {saved ? <Alert type="success" showIcon message="Company details updated." /> : null}

        <Form form={form} layout="vertical">
          <Form.Item name="companyId" label="Company ID">
            <Input disabled />
          </Form.Item>
          <Form.Item name="companyName" label="Company Name" rules={[{ required: true, message: 'Company name is required' }]}>
            <Input disabled={!canEditBusinessDetails} />
          </Form.Item>
          <Form.Item
            name="companyABN"
            label="Company ABN"
            rules={[
              { required: true, message: 'Company ABN is required' },
              { pattern: /^\d{11}$/, message: 'ABN must be 11 digits' },
            ]}
          >
            <Input disabled={!canEditBusinessDetails} />
          </Form.Item>
          <Form.List name="companyPhoneNumbers">
            {(fields, { add, remove }) => (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Company Phone Numbers</Text>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <Form.Item
                      name={[field.name, 'number']}
                      rules={[{ required: true, message: 'Phone number is required' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input disabled={!canEditBusinessDetails} placeholder={`Phone number ${index + 1}`} />
                    </Form.Item>
                    {canEditBusinessDetails ? (
                      <MinusCircleOutlined
                        onClick={() => remove(field.name)}
                        style={{ color: '#ff4d4f', fontSize: 18, cursor: 'pointer', flexShrink: 0 }}
                      />
                    ) : null}
                  </div>
                ))}
                {canEditBusinessDetails ? (
                  <Button
                    type="dashed"
                    onClick={() => add({ number: '' })}
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add phone number
                  </Button>
                ) : null}
              </div>
            )}
          </Form.List>
          <Form.List name="locations">
            {(fields, { add, remove }) => (
              <div>
                <Text strong>Company Locations</Text>
                {fields.map((field, index) => (
                  <Card key={field.key} size="small" style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Radio
                        checked={defaultLocIndex === index}
                        disabled={!canEditBusinessDetails}
                        onChange={() => {
                          setDefaultLocIndex(index);
                          const locs = form.getFieldValue('locations') || [];
                          form.setFieldValue('locations', locs.map((l, i) => ({ ...l, defaultLocation: i === index ? 'Y' : 'N' })));
                        }}
                      >
                        <Text strong style={{ fontSize: 13 }}>Default Location</Text>
                      </Radio>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      <Form.Item name={[field.name, 'streetNo']} label={`Street No ${index + 1}`}>
                        <Input disabled={!canEditBusinessDetails} placeholder="Street no" />
                      </Form.Item>
                      <Form.Item name={[field.name, 'streetName']} label="Street Name">
                        <Input disabled={!canEditBusinessDetails} placeholder="Street name" />
                      </Form.Item>
                      <Form.Item name={[field.name, 'city']} label="City">
                        <Input disabled={!canEditBusinessDetails} placeholder="City" />
                      </Form.Item>
                      <Form.Item name={[field.name, 'country']} label="Country">
                        <Input disabled={!canEditBusinessDetails} placeholder="Country" />
                      </Form.Item>
                    </div>
                    {canEditBusinessDetails ? (
                      <Button
                        type="link"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                        style={{ padding: 0 }}
                      >
                        Remove location
                      </Button>
                    ) : null}
                  </Card>
                ))}
                {canEditBusinessDetails ? (
                  <Button type="dashed" onClick={() => add({ streetNo: '', streetName: '', city: '', country: '' })} icon={<PlusOutlined />} style={{ marginTop: 12 }}>
                    Add location
                  </Button>
                ) : null}
              </div>
            )}
          </Form.List>
          <Form.Item name="brandImageUrl" label="Brand Image URL">
            <Input disabled />
          </Form.Item>
        </Form>

        {(imageData.localUrl || form.getFieldValue('brandImageUrl')) ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
            <div style={{ position: 'relative' }}>
              <img
                src={imageData.localUrl || form.getFieldValue('brandImageUrl')}
                alt="Brand"
                style={{ width: 200, height: 200, borderRadius: 10, objectFit: 'cover' }}
              />
              {canEditBusinessDetails ? (
                <button
                  type="button"
                  onClick={removeImage}
                  style={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 30,
                    height: 30,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 18,
                    lineHeight: '30px',
                    textAlign: 'center',
                  }}
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {canEditBusinessDetails ? (
          <div style={{ width: 220 }}>
            <input
              id={brandImageInputId}
              ref={brandImageInputRef}
              type="file"
              accept="image/*"
              onChange={pickImage}
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            />
            <Button block htmlType="button" onClick={openBrandImagePicker} loading={uploadingImage} disabled={saving || uploadingImage}>
              {uploadingImage ? 'Uploading Brand Image' : 'Select Brand Image'}
            </Button>
          </div>
        ) : null}

        <Button type="primary" htmlType="button" loading={saving} onClick={handleSave} disabled={!canEditBusinessDetails || uploadingImage}>
          Save Company Details
        </Button>
      </Space>
    </Card>
  );
}